import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { C, pageVariants, staggerContainer, staggerItem, shared } from '../theme/space';

const STAT_COLORS = {
  'Total Leads':  { glow: '#818cf8', bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.25)' },
  'Interested':   { glow: '#fb923c', bg: 'rgba(251,146,60,0.1)',  border: 'rgba(251,146,60,0.25)' },
  'Follow-up':    { glow: '#fbbf24', bg: 'rgba(251,191,36,0.1)',  border: 'rgba(251,191,36,0.25)' },
  'Enrolled':     { glow: '#34d399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.25)' },
  'Want Human':   { glow: '#f472b6', bg: 'rgba(244,114,182,0.1)', border: 'rgba(244,114,182,0.25)' },
};

const WA_CONFIG = {
  ready:         { border: 'rgba(52,211,153,0.4)',  bg: 'rgba(52,211,153,0.06)',  color: '#34d399', label: 'CONNECTED',      text: 'Bot is live and auto-replying to incoming messages.' },
  authenticated: { border: 'rgba(251,191,36,0.4)',  bg: 'rgba(251,191,36,0.06)',  color: '#fbbf24', label: 'AUTHENTICATING', text: 'Session confirmed — finalising connection...' },
  qr_ready:      { border: 'rgba(251,146,60,0.4)',  bg: 'rgba(251,146,60,0.06)',  color: '#fb923c', label: 'SCAN QR CODE',   text: 'Scan the QR below with your WhatsApp to go live.' },
  disconnected:  { border: 'rgba(248,113,113,0.4)', bg: 'rgba(248,113,113,0.06)', color: '#f87171', label: 'DISCONNECTED',   text: 'Click here to connect your WhatsApp account.' },
};

// Status badge for conversations
const STATUS_DOT = { interested: '#fb923c', 'follow-up': '#fbbf24', enrolled: '#34d399', 'not-interested': '#475569' };
function statusColor(s) { return STATUS_DOT[s] || C.primary; }

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function Dashboard() {
  const { qrCode, waStatus } = useSocket();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentChats, setRecentChats] = useState([]);

  useEffect(() => {
    api.get('/api/leads/stats').then(({ data }) => setStats(data)).catch(() => {});
    api.get('/api/chats?limit=5').then(({ data }) => setRecentChats(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (waStatus === 'ready') {
      api.get('/api/leads/stats').then(({ data }) => setStats(data)).catch(() => {});
      api.get('/api/chats?limit=5').then(({ data }) => setRecentChats(data.data || [])).catch(() => {});
    }
  }, [waStatus]);

  const statCards = stats
    ? [
        { label: 'Total Leads',  value: stats.total      },
        { label: 'Interested',   value: stats.interested  },
        { label: 'Follow-up',    value: stats.followUp    },
        { label: 'Enrolled',     value: stats.enrolled    },
        { label: 'Want Human',   value: stats.wantsHuman  },
      ]
    : [];

  const waCfg = WA_CONFIG[waStatus] || WA_CONFIG.disconnected;

  return (
    <motion.div style={s.page} variants={pageVariants} initial="hidden" animate="show">
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={shared.title}>Dashboard</h1>
          <p style={s.subtitle}>Mission control for your WhatsApp automation</p>
        </div>
        <div style={s.welcomeBadge}>
          <div style={s.welcomeDot} />
          <span style={s.welcomeText}>Welcome back, {user?.name || user?.email}</span>
        </div>
      </div>

      {/* WhatsApp Status Banner */}
      <Link to="/whatsapp" style={{ textDecoration: 'none' }}>
        <motion.div
          style={{ ...s.waBanner, borderColor: waCfg.border, background: waCfg.bg, cursor: 'pointer' }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.35 }}
          whileHover={{ scale: 1.01, background: waCfg.bg.replace('0.06', '0.09') }}
        >
          <div style={{ ...s.waStatusDot, background: waCfg.color, boxShadow: `0 0 10px ${waCfg.color}` }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: waCfg.color, fontSize: 12, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              WhatsApp — {waCfg.label}
            </div>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 2 }}>{waCfg.text}</div>
          </div>
          <div style={{ color: waCfg.color, fontSize: 18, fontWeight: 700 }}>&rarr;</div>
        </motion.div>
      </Link>

      {/* QR Code */}
      {waStatus === 'qr_ready' && qrCode && (
        <motion.div
          style={s.qrContainer}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <h2 style={{ margin: '0 0 6px', color: C.text, fontSize: 18 }}>Scan with WhatsApp</h2>
          <p style={{ color: C.textMuted, marginBottom: 20, fontSize: 13 }}>
            Open WhatsApp → Linked Devices → Link a Device → scan below.
          </p>
          <div style={s.qrWrapper}>
            <QRCodeSVG value={qrCode} size={240} />
          </div>
        </motion.div>
      )}

      {/* Stat Cards */}
      {statCards.length > 0 && (
        <motion.div
          style={s.statsGrid}
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          {statCards.map((sc) => {
            const theme = STAT_COLORS[sc.label] || { glow: C.primary, bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.25)' };
            return (
              <motion.div
                key={sc.label}
                style={{ ...s.statCard, background: theme.bg, borderColor: theme.border }}
                variants={staggerItem}
              >
                <span style={{ ...s.statValue, color: theme.glow, textShadow: `0 0 20px ${theme.glow}66` }}>
                  {sc.value ?? '—'}
                </span>
                <span style={s.statLabel}>{sc.label}</span>
                <div style={{ ...s.statGlow, background: theme.glow + '22' }} />
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Recent Conversations */}
      <motion.div
        style={s.section}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Recent Conversations</h2>
          <span style={s.sectionCount}>{recentChats.length} conversations</span>
        </div>
        {recentChats.length === 0 ? (
          <p style={s.empty}>No conversations yet. Start the bot and send a WhatsApp message.</p>
        ) : (
          <div style={s.chatList}>
            {recentChats.map((c) => {
              const last = c.lastMessage;
              const lead = c.lead;
              const phone = c.phone.replace('@c.us', '');
              const initials = lead?.name?.[0]?.toUpperCase() || phone[0];
              return (
                <div key={c._id} style={s.chatRow}>
                  <div style={s.chatAvatar}>{initials}</div>
                  <div style={s.chatInfo}>
                    <span style={s.chatName}>{lead?.name || phone}</span>
                    <span style={s.chatLast}>
                      {last
                        ? (last.role === 'assistant' ? 'Bot: ' : 'User: ') + last.content.slice(0, 55) + '...'
                        : 'No messages'}
                    </span>
                  </div>
                  <div style={s.chatMeta}>
                    <span style={{ ...s.badge, background: statusColor(lead?.status) + '22', color: statusColor(lead?.status), border: `1px solid ${statusColor(lead?.status)}44` }}>
                      {lead?.status || 'new'}
                    </span>
                    <span style={s.chatTime}>{formatTime(c.lastMessageAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

const s = {
  page: {
    padding: '28px 32px',
    flex: 1,
    overflowY: 'auto',
    background: 'transparent',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  subtitle: {
    color: C.textMuted,
    fontSize: 13,
    marginTop: 4,
  },
  welcomeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    background: 'rgba(99,102,241,0.08)',
    border: '1px solid rgba(99,102,241,0.14)',
    borderRadius: 20,
  },
  welcomeDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: C.success,
    boxShadow: `0 0 8px ${C.success}`,
  },
  welcomeText: {
    fontSize: 13,
    color: C.text,
    fontWeight: 500,
  },
  waBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 18px',
    borderRadius: 11,
    border: '1px solid',
    marginBottom: 24,
    backdropFilter: 'blur(8px)',
  },
  waStatusDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  qrContainer: {
    background: 'rgba(6, 12, 28, 0.9)',
    border: `1px solid rgba(56,189,248,0.25)`,
    borderRadius: 12,
    padding: 28,
    marginBottom: 24,
    textAlign: 'center',
    backdropFilter: 'blur(12px)',
  },
  qrWrapper: {
    display: 'inline-block',
    padding: 16,
    background: '#fff',
    borderRadius: 12,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
    gap: 14,
    marginBottom: 28,
  },
  statCard: {
    borderRadius: 12,
    padding: '22px 18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    border: '1px solid',
    position: 'relative',
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
  },
  statValue: {
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: -1,
    position: 'relative',
    zIndex: 1,
  },
  statLabel: {
    fontSize: 11,
    color: C.textMuted,
    textAlign: 'center',
    fontWeight: 500,
    letterSpacing: 0.2,
    position: 'relative',
    zIndex: 1,
  },
  statGlow: {
    position: 'absolute',
    bottom: -20,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 60,
    borderRadius: '50%',
    filter: 'blur(20px)',
  },
  section: {
    background: 'rgba(6, 12, 28, 0.88)',
    borderRadius: 12,
    padding: '20px 22px',
    border: '1px solid rgba(99,102,241,0.12)',
    backdropFilter: 'blur(12px)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: C.text,
    letterSpacing: -0.2,
  },
  sectionCount: {
    fontSize: 12,
    color: C.textMuted,
    background: 'rgba(99,102,241,0.08)',
    padding: '3px 10px',
    borderRadius: 10,
    border: '1px solid rgba(99,102,241,0.12)',
  },
  empty: { color: C.textMuted, fontSize: 14 },
  chatList: { display: 'flex', flexDirection: 'column' },
  chatRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '11px 8px',
    borderBottom: '1px solid rgba(99,102,241,0.06)',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'background 0.15s',
  },
  chatAvatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 14,
    color: C.primary,
    flexShrink: 0,
  },
  chatInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    overflow: 'hidden',
  },
  chatName: { fontWeight: 600, fontSize: 13, color: C.text },
  chatLast: { fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  chatMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  badge: { fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 600 },
  chatTime: { fontSize: 10, color: C.textFaint },
};
